import { useCallback, useEffect, useRef, useState } from "react";
import { protectedGet, protectedPost } from "@/lib/api";

export function useWorkerRuntime(agentId, onRuntime) {
  const [data, setData] = useState(null), [error, setError] = useState("");
  const mounted = useRef(true), snapshotCallback = useRef(onRuntime), requestId = useRef(0);
  useEffect(() => { snapshotCallback.current = onRuntime; }, [onRuntime]);
  const load = useCallback(async () => {
    const sequence = ++requestId.current;
    try {
      const result = await protectedGet(`/agents/${agentId}/runtime`);
      if (!mounted.current || sequence !== requestId.current) return;
      setData(result); setError(""); snapshotCallback.current?.(result.runtime);
    } catch (failure) { if (mounted.current && sequence === requestId.current) setError(failure.message); }
  }, [agentId]);
  useEffect(() => {
    mounted.current = true; load();
    const timer = setInterval(load, 1500);
    return () => { mounted.current = false; clearInterval(timer); };
  }, [load]);
  return { data, error, load, mounted };
}

export function useWorkerAction(agentId, state, onChanged) {
  const [confirmation, setConfirmation] = useState(null), [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const trigger = useRef(null), confirmButton = useRef(null);
  useEffect(() => { if (confirmation) confirmButton.current?.focus(); }, [confirmation]);
  const cancel = () => { if (submitting) return; setConfirmation(null); trigger.current?.focus(); };
  const request = (action, command, element) => { trigger.current = element || document.activeElement; setActionError(""); setConfirmation({ action, command }); };
  const confirm = async () => {
    if (submitting || !confirmation) return;
    setSubmitting(true); setActionError("");
    try {
      const path = confirmation.action === "run" ? "run" : `lifecycle/${confirmation.action}`;
      await protectedPost(`/agents/${agentId}/${path}`, { confirm: true, ...(confirmation.command ? { command: confirmation.command } : {}) });
      if (state.mounted.current) { setConfirmation(null); await state.load(); onChanged?.(); }
    } catch (failure) { if (state.mounted.current) setActionError(failure.message); }
    finally { if (state.mounted.current) setSubmitting(false); }
  };
  return { confirmation, submitting, actionError, confirmButton, cancel, request, confirm };
}
